import { useEffect, useState } from 'react';

import { CopyIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useMediaQuery } from '@nesso/ui/hooks/use-media-query';
import { copyToClipboard } from '@nesso/ui/lib/clipboard';
import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@nesso/ui/primitives/dropdown-menu';

import { formatPhone } from '@/shared/lib/format-utils';
import { TooltipButton } from '@/shared/ui/common/tooltip-button';

import { COPIED_FEEDBACK_MS } from '../config/reach-contact.config';
import { getDialablePhone } from '../lib/get-dialable-phone';

type Props = {
    kind: 'email' | 'phone';
    value: string;
    size?: 'base' | 'sm';
};

export function ReachContactValue({ kind, value, size = 'base' }: Props) {
    const isTouch = useMediaQuery('(pointer: coarse)');
    const [isOpen, setIsOpen] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isTipOpen, setIsTipOpen] = useState(false);
    const [copyCount, setCopyCount] = useState(0);
    const text = kind === 'phone' ? formatPhone(value) : value;
    const dialable = kind === 'phone' ? getDialablePhone(value) : null;
    const isActionable = kind === 'email' || dialable !== null;
    const hasMenu = isTouch && isActionable;

    useEffect(() => {
        if (copyCount === 0) return;

        const timeout = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
        return () => clearTimeout(timeout);
    }, [copyCount]);

    // Without this the menu would reopen on its own when the value turns touch-driven again.
    if (!hasMenu && isOpen) setIsOpen(false);

    if (!isActionable) return text;

    const buttonClassName = cn('-mx-1 rounded-none px-1', size === 'sm' && 'text-sm');
    const noun = kind === 'phone' ? 'Phone number' : 'Email';

    const copy = async () => {
        const copied = await copyToClipboard(text);

        if (!copied) {
            toast.message(`Could not copy the ${noun.toLowerCase()}`);
        } else if (isTouch) {
            toast.message(`${noun} copied`);
        } else {
            setIsCopied(true);
            setCopyCount((count) => count + 1);
        }
    };

    if (!hasMenu) {
        return (
            <>
                <TooltipButton
                    type="button"
                    variant="inline-copy"
                    size="inline"
                    className={buttonClassName}
                    aria-label={`Copy ${text}`}
                    data-copied={isCopied || undefined}
                    onClick={copy}
                    tooltip="Click to copy"
                    tooltipProps={{
                        delayDuration: 0,
                        open: isTipOpen && !isCopied,
                        onOpenChange: setIsTipOpen,
                    }}
                    tooltipContentProps={{
                        side: 'top',
                        showArrow: false,
                        className: 'px-2 py-1 text-sm',
                    }}
                >
                    <span className={isCopied ? 'invisible' : undefined}>{text}</span>
                    {isCopied && (
                        <span className="absolute inset-0 flex items-center justify-center gap-1">
                            <CopyIcon className={size === 'sm' ? 'size-3.25' : 'size-3.75'} />
                            copied
                        </span>
                    )}
                </TooltipButton>
                <span role="status" className="sr-only">
                    {isCopied ? `${noun} copied` : ''}
                </span>
            </>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                {/* Radix opens on pointerdown, which a touch scroll starting on the value would trigger.
                    Preventing it makes Radix skip its handler, so the menu opens on click instead. */}
                <Button
                    type="button"
                    variant="inline"
                    size="inline"
                    className={buttonClassName}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => setIsOpen(true)}
                >
                    {text}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent variant="button-group" side="top" align="start">
                <DropdownMenuItem variant="button-group" onSelect={copy}>
                    Copy
                </DropdownMenuItem>
                {kind === 'email' ? (
                    <DropdownMenuItem variant="button-group" asChild>
                        <a href={`mailto:${encodeURIComponent(value).replace('%40', '@')}`}>
                            Email
                        </a>
                    </DropdownMenuItem>
                ) : (
                    <>
                        <DropdownMenuItem variant="button-group" asChild>
                            <a href={`tel:${dialable}`}>Call</a>
                        </DropdownMenuItem>
                        <DropdownMenuItem variant="button-group" asChild>
                            <a href={`sms:${dialable}`}>Text</a>
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
